import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { MainLayout } from "@/components/layout/MainLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { listEmailDeliveries } from "@/lib/emailDeliveries.functions";

const T = "Email Deliveries | Statlign Admin";
const D = "Track every recap, receipt and Premium email: when it was sent, and whether it was delivered or bounced.";

export const Route = createFileRoute("/admin/email-deliveries")({
  head: () => ({
    meta: [
      { title: T },
      { name: "description", content: D },
      { property: "og:title", content: T },
      { property: "og:description", content: D },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <MainLayout>
      <ProtectedRoute requiredRole="admin">
        <EmailDeliveries />
      </ProtectedRoute>
    </MainLayout>
  ),
});

const variant = (s: string) =>
  s === "Delivered" ? "default" : s === "Bounced" || s === "Failed" ? "destructive" : "secondary";

function EmailDeliveries() {
  const fn = useServerFn(listEmailDeliveries);
  const q = useQuery({ queryKey: ["admin-email-deliveries"], queryFn: () => fn(), refetchInterval: 60_000 });
  const rows = q.data?.rows ?? [];
  const count = (s: string) => rows.filter((r) => r.status === s).length;

  return (
    <div className="container mx-auto max-w-5xl space-y-6 px-4 py-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Email deliveries</h1>
          <p className="text-sm text-muted-foreground">{D}</p>
        </div>
        <Button variant="outline" onClick={() => q.refetch()} disabled={q.isFetching}>
          {q.isFetching ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {["Delivered", "Sent", "Bounced", "Failed"].map((s) => (
          <Card key={s}>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">{s}</div>
              <div className="text-2xl font-bold">{count(s)}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {q.data?.eventsError && (
        <p className="text-sm text-destructive">Couldn't load delivery events from Brevo ({q.data.eventsError}). Send times are still shown.</p>
      )}
      {q.error && <p className="text-sm text-destructive">We couldn't load email deliveries. Please refresh in a moment.</p>}

      <Card>
        <CardHeader><CardTitle className="text-base">Latest 200 emails</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto p-0">
          {q.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading email history…</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No recap, receipt or Premium emails have been sent yet. They appear here as soon as the live site sends one.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr><th className="p-3">Sent</th><th className="p-3">Email</th><th className="p-3">Recipient</th><th className="p-3">Status</th><th className="p-3">Events</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 align-top">
                    <td className="whitespace-nowrap p-3">{new Date(r.sentAt).toLocaleString()}</td>
                    <td className="p-3">{r.email}</td>
                    <td className="p-3 break-all">{r.recipient}</td>
                    <td className="p-3"><Badge variant={variant(r.status)}>{r.status}</Badge></td>
                    <td className="p-3 text-xs text-muted-foreground">{r.error ?? (r.events.join(", ") || "—")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
